import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  Box,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
// Internal Dependencies
import { DisplayAggregateQuestionDto } from '@keyops-cep/api-client';
import { Chart, ChartConfiguration } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { toPng } from 'html-to-image';

import { colors } from '../../../assets/custom-theme';
import { ISurveySparrowAnswerData } from '../../../pages/EngagementDetails/types';
import {
  extractLabelFrequenciesForMatrix,
  getHeaderLabelsFromChoices,
} from '../../../utils/functions/visualization-utils';
import { VisualizationChartRef } from '..';

const cellStyle = {
  textTransform: 'capitalize',
  fontSize: '12px',
  padding: '8px',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  height: '100px',
  maxWidth: '130px',
  minWidth: '100px',
  textAlign: 'center',
};

const colorPalette = [
  colors.keyops.blue,
  colors.keyops.teal,
  colors.keyops.darkerBlue,
  colors.keyops.visualizationBlue,
  colors.keyops.gray500,
  colors.keyops.purple,
];

type Cell =
  | { label: string } // for row headers
  | Record<string, number> //  for pie chart data - e.g., { "Better": 4, "Worse": 2 }
  | null; // for missing cells

type MatrixRow = Cell[];
type MatrixBody = MatrixRow[];

type Props = {
  question: DisplayAggregateQuestionDto;
  questionAnswers: (ISurveySparrowAnswerData | undefined)[];
};

const MultiSelectPieMatrix = forwardRef<VisualizationChartRef, Props>(
  ({ question, questionAnswers }, ref) => {
    const renderId = useRef(Math.random().toString(36).substring(2, 9));
    const tableRef = useRef<HTMLDivElement | null>(null);
    const chartInstances = useRef<Record<string, Chart<'pie'> | null>>({}); // Track multiple chart instances

    // 1. Extract header labels and build matrix
    const { headers, matrix } = useMemo(() => {
      const headerLabels = getHeaderLabelsFromChoices(question.choices);

      const headers = headerLabels;
      const frequencies = extractLabelFrequenciesForMatrix(
        questionAnswers,
        headerLabels
      );

      const matrix: MatrixBody = (question.components || []).reduce<MatrixBody>(
        (acc, component) => {
          const cells = headerLabels.map((type) => {
            const cellData = frequencies.find(
              (f) =>
                f.componentId === component.surveySparrowId &&
                typeof f[type] === 'object' &&
                Object.keys(f[type] || {}).length > 0
            );
            return cellData &&
              typeof cellData[type] === 'object' &&
              cellData[type] !== null
              ? (cellData[type] as Record<string, number>)
              : null;
          });

          // Skip row if all mapped cells are null
          const hasAnyData = cells.some((cell) => cell !== null);
          if (!hasAnyData) return acc;

          const row: MatrixRow = [{ label: component.label }, ...cells];
          acc.push(row);
          return acc;
        },
        []
      );

      return { headers, matrix };
    }, [question, questionAnswers]);

    // 2. Map dropdown labels to colors - Maintains color+label pairing accross multiple charts
    const [uniqueDropdownLabels, labelColorMap] = useMemo(() => {
      const labels: string[] = [];
      const map: Record<string, string> = {};

      (question.choices ?? []).forEach((c) => {
        const parts = c.label.split(':');
        if (parts.length > 1) {
          const label = parts[1].trim();
          if (!map[label]) {
            map[label] = colorPalette[labels.length % colorPalette.length];
            labels.push(label);
          }
        }
      });

      return [labels, map];
    }, [question.choices]);

    // 3. Render pie charts into canvas elements
    useEffect(() => {
      matrix.forEach((row, i) => {
        row.forEach((cell, j) => {
          const chartId = `pie-chart-${renderId.current}-${i}-${j}`;
          const canvas = document.getElementById(chartId) as HTMLCanvasElement;
          if (!canvas || !cell || typeof cell !== 'object') return;

          const data = {
            labels: Object.keys(cell),
            datasets: [
              {
                data: Object.values(cell),
                backgroundColor: Object.keys(cell).map(
                  (label) => labelColorMap[label]
                ),
              },
            ],
          };

          if (chartInstances.current[chartId]) {
            // destroy prev chart - chartjs doesn't allow for 2 chartInstances with the same id
            chartInstances.current[chartId]?.destroy();
          }

          chartInstances.current[chartId] = new Chart(canvas, {
            type: 'pie',
            data,
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const value = ctx.raw as number;
                      const dataset = ctx.dataset;
                      const total = dataset.data.reduce(
                        (sum: number, val: number) => sum + val,
                        0
                      );
                      const percent =
                        total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                      return ` ${value} (${percent}%)`;
                    },
                  },
                },
                datalabels: {
                  color: '#fff',
                  font: {
                    weight: 'bold',
                    size: 12,
                  },
                  anchor: 'end',
                  align: 'start',
                  offset: 1,
                },
              },
            },
            plugins: [ChartDataLabels],
          } as ChartConfiguration<'pie', number[], string>);
        });
      });

      return () => {
        Object.values(chartInstances.current).forEach((chart) =>
          chart?.destroy()
        );
        chartInstances.current = {};
      };
    }, [matrix, labelColorMap]);

    // 4. Handle image export and copy
    useImperativeHandle(ref, () => {
      const createImageFromTable = async (): Promise<string | null> => {
        if (!tableRef.current) return null;

        const clone = tableRef.current.cloneNode(true) as HTMLDivElement;
        const originalTableWidth = getComputedStyle(tableRef.current).width;
        clone.style.height = 'auto';
        clone.style.maxHeight = 'none';
        clone.style.overflowY = 'visible';
        clone.style.overflow = 'hidden';
        clone.style.width = originalTableWidth;
        // Style the legend
        const style = document.createElement('style');
        style.textContent = `
          .legend {
            margin-top: 0 !important;
          }
          .legend-inner {
            margin-left: 16px !important;
          }
        `;
        clone.appendChild(style);

        // Replace canvases with images
        const originalCanvases = tableRef.current.querySelectorAll('canvas');
        const cloneCanvases = clone.querySelectorAll('canvas');

        originalCanvases.forEach((originalCanvas, i) => {
          const dataUrl = originalCanvas.toDataURL('image/png');
          const img = new Image();
          img.src = dataUrl;
          img.width = originalCanvas.width;
          img.height = originalCanvas.height;

          const clonedCanvas = cloneCanvases[i];
          clonedCanvas.replaceWith(img);
        });

        document.body.appendChild(clone);
        try {
          return await toPng(clone);
        } catch (error) {
          console.error('Failed to export image', error);
        } finally {
          document.body.removeChild(clone);
        }
        return null;
      };

      return {
        onDownload: async (name: string) => {
          const dataUrl = await createImageFromTable();
          if (dataUrl) {
            const link = document.createElement('a');
            link.download = `${name}.png`;
            link.href = dataUrl;
            link.click();
          }
        },
        onCopyToClipboard: async () => {
          const dataUrl = await createImageFromTable();
          if (dataUrl) {
            const response = await fetch(dataUrl);
            const blob = await response.blob();
            const item = new ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([item]);
            console.log('Image copied to clipboard');
          }
        },
      };
    });

    return (
      <Box
        ref={tableRef}
        data-testid="non-numeric-grid-test-id"
        display="flex"
        flexDirection="column"
        h="100%"
      >
        <Box flex="1" minH={0} overflowY="auto">
          <TableContainer
            bg={useColorModeValue('white', 'gray.700')}
            borderWidth="thin"
            borderColor="gray.200"
            borderRadius="lg"
            overflowX="auto"
          >
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th sx={cellStyle}></Th>
                  {headers.map((header, idx) => (
                    <Th key={`header-${idx}`} sx={cellStyle}>
                      {header}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {matrix.map((row, i) => (
                  <Tr key={`row-${i}`}>
                    {row.map((cell, j) => (
                      <Td key={`cell-${i}-${j}`} sx={cellStyle}>
                        {cell?.label ? (
                          cell.label
                        ) : cell ? (
                          <canvas
                            id={`pie-chart-${renderId.current}-${i}-${j}`}
                            data-testid={`pie-chart-${i}-${j}-test-id`}
                            width={100}
                            height={100}
                          />
                        ) : (
                          'N/A'
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* Sticky Legend */}
        <Box
          className="legend" // used to inject style for export/copy functions
          mt={2}
          py={2}
          borderTop="1px solid"
          borderColor="gray.200"
          bg={useColorModeValue('white', 'gray.700')}
          position="sticky"
          bottom="0"
          zIndex={1}
        >
          <Box
            className="legend-inner"
            display="flex"
            gap={3}
            alignItems="center"
            fontSize="sm"
            flexWrap="wrap"
            ml={4}
          >
            {uniqueDropdownLabels.map((label, i) => (
              <Box
                key={`legend-${i}`}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Box
                  w="16px"
                  h="16px"
                  bg={labelColorMap[label]}
                  borderRadius="sm"
                />
                <Box>{label}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }
);

MultiSelectPieMatrix.displayName = 'MultiSelectPieMatrix';
export default MultiSelectPieMatrix;
